"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import { createKudoAction } from "@/actions/contracts-actions";
import { useRouter } from "next/navigation";
import {
  Heart,
  Send,
  Search,
  Sparkles,
  MessageSquare,
  Award,
  User,
  Target,
  Briefcase,
} from "lucide-react";

interface AgentItem {
  id: string;
  name: string;
  role: string;
}

interface KudoItem {
  id: string;
  senderId: string;
  receiverId: string;
  nodeId: string;
  message: string;
  createdAt: string;
  sender: { name: string; role: string };
  receiver: { name: string; role: string };
}

interface ProjectItem {
  id: string;
  title: string;
}

interface GoalItem {
  id: string;
  title: string;
}

interface KudosClientProps {
  initialKudos: KudoItem[];
  agents: AgentItem[];
  projects: ProjectItem[];
  goals: GoalItem[];
  currentAgentId: string;
}

const DEFAULT_MOCK_KUDOS = (agents: AgentItem[]): KudoItem[] => {
  const getAgent = (name: string, role: string) => {
    const found = agents.find((a) =>
      a.name.toLowerCase().includes(name.toLowerCase()),
    );
    return found || { id: `mock_${name.toLowerCase()}`, name, role };
  };

  const a1 = getAgent("Alessio Corrado", "Owner / Director");
  const a2 = getAgent("Giulia Rossi", "Senior Frontend Engineer");
  const a3 = getAgent("Marco Bianchi", "DevOps Engineer");

  return [
    {
      id: "kudo_1",
      senderId: a1.id,
      receiverId: a2.id,
      nodeId: "Swimlane Layout Optimization",
      message:
        "Grazie Giulia per l'incredibile lavoro sul refactoring del layout dei swimlanes. La visualizzazione in 2D adesso è super fluida e reattiva!",
      createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      sender: { name: a1.name, role: a1.role },
      receiver: { name: a2.name, role: a2.role },
    },
    {
      id: "kudo_2",
      senderId: a2.id,
      receiverId: a3.id,
      nodeId: "Database Migration",
      message:
        "Grande Marco! Ottima gestione del tuning degli indici nel database e dei vincoli RLS durante il rilascio. Zero downtime!",
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      sender: { name: a2.name, role: a2.role },
      receiver: { name: a3.name, role: a3.role },
    },
    {
      id: "kudo_3",
      senderId: a3.id,
      receiverId: a1.id,
      nodeId: "Supabase Auth Foundation",
      message:
        "Grazie Alessio per la guida chiara e il supporto costante sul flusso delle sessioni OAuth. Ottimo spirito di squadra!",
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      sender: { name: a3.name, role: a3.role },
      receiver: { name: a1.name, role: a1.role },
    },
  ];
};

export function KudosClient({
  initialKudos,
  agents,
  projects,
  goals,
  currentAgentId,
}: KudosClientProps) {
  const router = useRouter();

  const [kudosList, setKudosList] = useState<KudoItem[]>(() => {
    return initialKudos.length > 0 ? initialKudos : DEFAULT_MOCK_KUDOS(agents);
  });

  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form State
  const [receiverId, setReceiverId] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [message, setMessage] = useState("");

  // Custom sender state if user has no assigned agent (mock mode/admin mode)
  const [customSenderId, setCustomSenderId] = useState(
    currentAgentId || agents[0]?.id || "",
  );

  useEffect(() => {
    if (initialKudos.length > 0) {
      setKudosList(initialKudos);
    }
  }, [initialKudos]);

  const handleOpenModal = () => {
    setReceiverId("");
    setNodeId("");
    setMessage("");
    setError("");
    setIsModalOpen(true);
  };

  const handleSendKudo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const sender = currentAgentId || customSenderId;
    if (!sender) {
      setError("Impossibile determinare l'agente mittente.");
      return;
    }
    if (!receiverId) {
      setError("Seleziona il collega che vuoi ringraziare.");
      return;
    }
    if (sender === receiverId) {
      setError("Non puoi inviare un Kudo a te stesso!");
      return;
    }
    if (!message.trim() || message.length < 10) {
      setError("Il messaggio deve essere lungo almeno 10 caratteri.");
      return;
    }

    setLoading(true);

    try {
      const res = await createKudoAction({
        senderId: sender,
        receiverId,
        nodeId,
        message,
      });

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
        router.refresh();

        if (initialKudos.length === 0) {
          const sAgent = agents.find((a) => a.id === sender);
          const rAgent = agents.find((a) => a.id === receiverId);

          const newKudo: KudoItem = {
            id: res.id || `kudo_${Date.now()}`,
            senderId: sender,
            receiverId,
            nodeId,
            message,
            createdAt: new Date().toISOString(),
            sender: sAgent
              ? { name: sAgent.name, role: sAgent.role }
              : { name: "Io", role: "member" },
            receiver: rAgent
              ? { name: rAgent.name, role: rAgent.role }
              : { name: "Collega", role: "member" },
          };

          setKudosList((prev) => [newKudo, ...prev]);
        }
      }
    } catch (err: any) {
      setError(err.message || "Errore durante l'invio del Kudo.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to format initials for avatars
  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  // Filter Kudos based on search
  const filteredKudos = kudosList.filter(
    (k) =>
      k.message.toLowerCase().includes(search.toLowerCase()) ||
      k.sender.name.toLowerCase().includes(search.toLowerCase()) ||
      k.receiver.name.toLowerCase().includes(search.toLowerCase()) ||
      k.nodeId.toLowerCase().includes(search.toLowerCase()),
  );

  // Stats calculation
  const totalKudos = kudosList.length;
  const recentKudos = kudosList.filter((k) => {
    const diff = Date.now() - new Date(k.createdAt).getTime();
    return diff < 7 * 24 * 60 * 60 * 1000; // last 7 days
  }).length;

  return (
    <div className="space-y-6">
      {/* Top dashboard metrics panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border border-border/60 bg-card">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Riconoscimenti Totali
              </span>
              <div className="text-3xl font-extrabold text-text-primary">
                {totalKudos}
              </div>
              <p className="text-xs text-text-muted">
                Kudos inviati dall'inizio dell'anno
              </p>
            </div>
            <div className="p-3.5 bg-primary-500/10 text-primary-500 rounded-xl">
              <Award className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border/60 bg-card">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Nuovi questa settimana
              </span>
              <div className="text-3xl font-extrabold text-text-primary">
                {recentKudos}
              </div>
              <p className="text-xs text-text-muted">Negli ultimi 7 giorni</p>
            </div>
            <div className="p-3.5 bg-success-soft text-success rounded-xl">
              <Heart className="h-6 w-6 fill-success/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border/60 bg-card">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                Team Member Attivi
              </span>
              <div className="text-3xl font-extrabold text-text-primary">
                {agents.length}
              </div>
              <p className="text-xs text-text-muted">
                Membri abilitati al peer recognition
              </p>
            </div>
            <div className="p-3.5 bg-info-soft text-info rounded-xl">
              <User className="h-6 w-6" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Control Bar */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca Kudos per mittente, destinatario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Button
          onClick={handleOpenModal}
          className="gap-1.5 shrink-0 w-full sm:w-auto"
        >
          <Send className="h-4 w-4" />
          Invia Kudos
        </Button>
      </div>

      {/* Kudos recognition feed */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredKudos.length === 0 ? (
          <div className="md:col-span-2 lg:col-span-3 flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center space-y-3">
            <Heart className="h-10 w-10 text-text-muted animate-pulse" />
            <h3 className="font-bold text-text-primary">Ancora nessun Kudos</h3>
            <p className="text-sm text-text-secondary max-w-sm">
              Sii il primo a ringraziare un collega per il supporto o per un
              lavoro ben fatto!
            </p>
            <Button onClick={handleOpenModal} size="sm" className="mt-2">
              Invia Kudos Ora
            </Button>
          </div>
        ) : (
          filteredKudos.map((kudo) => {
            const senderInitials = getInitials(kudo.sender.name);
            const receiverInitials = getInitials(kudo.receiver.name);

            return (
              <Card
                key={kudo.id}
                className="hover:shadow-sm hover:border-primary-500/20 transition-all border border-border bg-card flex flex-col justify-between"
              >
                <CardHeader className="p-4 pb-3 border-b border-border/40">
                  <div className="flex items-center gap-3">
                    {/* Visual Connection representation (Sender -> Receiver) */}
                    <div className="flex -space-x-2 select-none">
                      <div
                        className="h-9 w-9 rounded-full bg-primary-600 border-2 border-card text-text-on-primary flex items-center justify-center text-xs font-semibold shadow-sm"
                        title={`Sender: ${kudo.sender.name}`}
                      >
                        {senderInitials}
                      </div>
                      <div
                        className="h-9 w-9 rounded-full bg-accent border-2 border-card text-text-on-primary flex items-center justify-center text-xs font-semibold shadow-sm"
                        title={`Receiver: ${kudo.receiver.name}`}
                      >
                        {receiverInitials}
                      </div>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-text-primary truncate">
                        {kudo.sender.name}
                      </div>
                      <div className="text-[10px] text-text-muted flex items-center gap-0.5 mt-0.5">
                        <span>ha inviato un Kudo a</span>
                        <span className="font-bold text-text-secondary">
                          {kudo.receiver.name}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="p-4 py-4 flex-1 space-y-3">
                  <p className="text-sm text-text-secondary italic leading-relaxed">
                    "{kudo.message}"
                  </p>

                  {/* Associated Node or Object Badge */}
                  {kudo.nodeId && kudo.nodeId !== "system" && (
                    <div className="flex items-center gap-1.5 text-xs text-text-muted mt-2">
                      <Sparkles className="h-3.5 w-3.5 text-warning shrink-0" />
                      <span className="font-semibold text-text-secondary truncate">
                        {kudo.nodeId}
                      </span>
                    </div>
                  )}
                </CardContent>

                <CardFooter className="p-4 pt-2 border-t border-border/40 flex justify-between items-center text-[10px] text-text-muted">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Bacheca Kudos
                  </span>
                  <span>
                    {new Date(kudo.createdAt).toLocaleDateString("it-IT", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </CardFooter>
              </Card>
            );
          })
        )}
      </div>

      {/* Send Kudos Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invia un Kudos Riconoscimento</DialogTitle>
            <DialogDescription>
              Esprimi apprezzamento per un collega e collega il kudo a un
              progetto o obiettivo strategico.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendKudo} className="space-y-4 my-2">
            {/* Sender Selection for demonstration (if currentAgentId is empty) */}
            {!currentAgentId && (
              <div className="space-y-1.5">
                <Label htmlFor="kudo-sender">Invia come (Mittente)</Label>
                <Select
                  id="kudo-sender"
                  value={customSenderId}
                  onChange={(e) => setCustomSenderId(e.target.value)}
                  required
                >
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.role})
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {/* Receiver Selection */}
            <div className="space-y-1.5">
              <Label htmlFor="kudo-receiver">
                Collega da ringraziare (Destinatario)
              </Label>
              <Select
                id="kudo-receiver"
                value={receiverId}
                onChange={(e) => setReceiverId(e.target.value)}
                required
              >
                <option value="">-- Seleziona un membro del team --</option>
                {agents
                  .filter((a) => a.id !== (currentAgentId || customSenderId))
                  .map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.role})
                    </option>
                  ))}
              </Select>
            </div>

            {/* Context / Node association */}
            <div className="space-y-1.5">
              <Label htmlFor="kudo-node">
                Associa a un'attività o traguardo (Opzionale)
              </Label>
              <Select
                id="kudo-node"
                value={nodeId}
                onChange={(e) => setNodeId(e.target.value)}
              >
                <option value="">-- Nessuna associazione specifica --</option>

                {/* Projects group */}
                <optgroup label="Progetti & Lavori (Work)">
                  {projects.map((p) => (
                    <option key={p.id} value={p.title}>
                      💼 {p.title}
                    </option>
                  ))}
                </optgroup>

                {/* Goals group */}
                <optgroup label="Obiettivi Aziendali (Objective)">
                  {goals.map((g) => (
                    <option key={g.id} value={g.title}>
                      🎯 {g.title}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </div>

            {/* Recognition message */}
            <div className="space-y-1.5">
              <Label htmlFor="kudo-message">
                Il tuo messaggio di ringraziamento
              </Label>
              <textarea
                id="kudo-message"
                className="flex min-h-[100px] w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
                placeholder="es. Grazie per il supporto eccezionale nel debug delle query SQL e per aver dedicato tempo a spiegarmi le ottimizzazioni..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={400}
                required
              />
              <p className="text-[10px] text-text-muted">
                Scrivi un messaggio specifico e significativo. Almeno 10
                caratteri.
              </p>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2 border-t border-border/50">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading} className="gap-1.5">
                <Send className="h-3.5 w-3.5" />
                Invia Kudos
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
